// THE SENTENCES THIS CLIENT HOLDS ITSELF, EACH AS TYLER APPROVED IT (WEB-COPY-1).
//
// Tyler, 2026-09-29, on the website-wording approval page (https://claude.ai/artifact/XxjQvHhrXPEBHkM67Q3Smt, 22 items):
// "approve all sentences..". Every entry below is that page's wording, word for word, and carries its item number.
//
// WHY THESE ARE HELD HERE AND NOT SERVED. The server owns every sentence that states a fact or a decision
// (R226), and most of what this client shows arrives as `Copy` on a response. These cannot:
//   - the transport's own failures (items 7, 15, 16): a call that never completed, came back unreadable, or was written
//     against a contract this build cannot read has, by definition, no server sentence with it;
//   - sentences naming an act only a web page has ("refresh", items 15, 16, 18), which R112 keeps out of the one table
//     the app also reads;
//   - the two attestation FALLBACKS (items 11, 12), shown only when the server did not serve the attestation it
//     normally serves — and shown as words, never as a box to tick, because there is no served hash to record against;
//   - the rest, which no endpoint serves yet (items 1, 2, 4, 5, 6, 8, 9, 10, 14, 17, 19).
// Items 3 and 13 are NOT here. Each depends on a server fact — R-3's shape, the state's name — so the server states them
// (`evaluate.r3.savesYou` and `evaluate.r3.wouldChange.*`; `resolve.g2.noCard`) and the screens render what arrives.
// Items 20-22 are the server's own refusals (`checkout.*`) and are in its table too.
//
// ONE RECORD PER SENTENCE, NOT PER TABLE: a missing cell reads exactly like a cell that agrees (CLAUDE.md, R29). Each
// entry names its status, Tyler's words and the page item; `secondCopyOf` names where the words were first written when
// they are a copy (the app, `legalVersions.ts`, or the approved purchase policies), and `test/heldCopy.test.tsx` reads
// each of those sources and fails on a drift. `pendingCounsel` KEEPS the counsel flag where the source still carries it
// (items 1, 11, 12): Tyler's approval is of the words for this website, not a lawyer's read.
//
// IF A PIN FAILS, DO NOT EDIT THE PIN. A changed word is a new sentence: take it back to Tyler, and change the status.

/** What Tyler said, and when. The same for every entry but one: one reply approved the whole page, and a later one
 *  (`LAUNCH_NO_REMINDERS`, R189) replaced item 6's words for launch. */
export const WEB_COPY_1 = { by: "Tyler", on: "2026-09-29", quote: "approve all sentences.." } as const;

/**
 * R189 (B4) — THE WEBSITE LAUNCHES WITHOUT REMINDER EMAILS, SO EVERY WEBSITE SENTENCE THAT PROMISES ONE CHANGES UNTIL THE
 * REMINDER JOB (EM-3) EXISTS. Tyler's scope answer 1B ("1 B 2 B 3 yes", 2026-10-07). Four changes were offered word for
 * word and approved in one reply (docs/legal/WEB_PURCHASE_POLICIES.md on main at `0205637f`, "Launch without reminder
 * emails: four website changes (R189, 2026-10-07)"):
 *   1. A5 (`account.create.emailHelp`) and 2. A6 (`account.create.emailHelpWithNews`): new words, under this constant;
 *   3. G5, the settings page's reminders switch: NO new words — the row is not rendered and the page sends no reminders
 *      answer while `WEBSITE_REMINDERS_LIVE` (routes/steps/detailsRules.ts, the website's one "a reminder exists" switch)
 *      is false (routes/account/Settings.tsx); its words stay held in `ACCOUNT_COPY`, unrendered, for EM-3;
 *   4. `deadline.unverified`: new words, under this constant — no longer a second copy of ReminderService.swift's
 *      fallback, which the app keeps, because the app's phone reminders exist.
 * WEBSITE ONLY. The originals, each already approved, return with EM-3; `REMINDER_ORIGINALS` keeps them word for word, and
 * nothing renders them. IF A PIN FAILS, DO NOT EDIT THE PIN.
 */
export const LAUNCH_NO_REMINDERS = { by: "Tyler", on: "2026-10-07", quote: "yes to all four wording changes" } as const;

/**
 * B6b's TEN WEBSITE SENTENCES, APPROVED WORD FOR WORD (R189, Tyler, 2026-10-09). Drafted by B6b (R246) and held unshown
 * until this reply: five that stop promising the app's email sign-in, which ships after the website (R189 answer 2) — D4,
 * G10, K7, B9, K1 — and five that only correct the site's name from the marketing site's `proppeal.com` to the paid
 * website's `app.proppeal.com` — WEB-COPY-1 item 8, K5, K6, K12 and `account.error.popupBlocked`. Offered word for word in
 * docs/WEB_COPY_TODO.md (B6b) and approved as item 1 of one reply: "1. Approved" (docs/legal/WEB_PURCHASE_POLICIES.md on
 * main at `8463ed5f`, "The legal pages approved; ten website sentences; app purchase records"). These words are what
 * renders now; the words they replaced render nowhere, and are kept word for word only on the test side, where the record
 * needs them (`test/accountsPlan.ts` `ACCOUNTS_PLAN_BEFORE_B6B`, `test/approvalPage.ts` `APPROVAL_PAGE_BEFORE_B6B`, and
 * `test/heldCopy.test.tsx` section 8). IF A PIN FAILS, DO NOT EDIT THE PIN.
 */
export const B6B_SENTENCES_APPROVED = { by: "Tyler", on: "2026-10-09", quote: "1. Approved" } as const;

export interface HeldSentence {
  readonly text: string;
  readonly status: "approved";
  /** WEB-COPY-1's reply and its page item — or, for a sentence a later reply replaced, that reply and the same item. */
  readonly approval: (typeof WEB_COPY_1 | typeof LAUNCH_NO_REMINDERS | typeof B6B_SENTENCES_APPROVED) & { readonly item: number };
  /** The file and symbol these words were first written in, when this is a second copy of them; null when new. */
  readonly secondCopyOf: string | null;
  /** The source's own counsel marker, kept beside the approval; null when the source carries none. */
  readonly pendingCounsel: string | null;
}

function approved(
  item: number,
  text: string,
  from: {
    secondCopyOf?: string; pendingCounsel?: string;
    approval?: typeof WEB_COPY_1 | typeof LAUNCH_NO_REMINDERS | typeof B6B_SENTENCES_APPROVED;
  } = {},
): HeldSentence {
  return {
    text,
    status: "approved",
    approval: { ...(from.approval ?? WEB_COPY_1), item },
    secondCopyOf: from.secondCopyOf ?? null,
    pendingCounsel: from.pendingCounsel ?? null,
  };
}

const ASSENT_COPY = "AppealAce/Features/Onboarding/AssentCopy.swift `AssentCopy.onboardingCheckbox`";
const MODELS = "AppealAce/Models/Models.swift `OutcomeResult`";
const POLICIES = "docs/legal/WEB_PURCHASE_POLICIES_APPROVED_2026-09-28.html";
const ATTESTATIONS_V2 = "functions/src/legalVersions.ts `ATTESTATIONS_V2`";
const COUNSEL_R19 = "PROVISIONAL PENDING COUNSEL (R19), as AssentCopy.swift marks it";
const COUNSEL_V2 = "PENDING COUNSEL REVIEW before release, as legalVersions.ts marks the V2 attestations";

export const HELD_COPY = {
  // ── /account ──
  "assent.onboardingCheckbox": approved(1, "I'm 18 or older and I agree to the Terms of Service and Privacy Policy", {
    secondCopyOf: ASSENT_COPY,
    pendingCounsel: COUNSEL_R19,
  }),
  "assent.standingDisclosure": approved(
    2,
    "PROppeal is a document-preparation tool — not a law firm, appraiser, or your representative. You file your own appeal, and no result is guaranteed.",
    { secondCopyOf: "AppealAce/Shared/Disclaimers.swift `Disclaimers.onboardingSummary`" },
  ),

  // ── /outcome: the six results, each with the app's one-line description (item 4), and the sharing sentence (item 5) ──
  "outcomes.labels.filed.title": approved(4, "Filed", { secondCopyOf: `${MODELS} .title` }),
  "outcomes.labels.filed.subtitle": approved(4, "Submitted to the county — waiting on a decision", { secondCopyOf: `${MODELS} .pickerSubtitle` }),
  "outcomes.labels.won.title": approved(4, "Won", { secondCopyOf: `${MODELS} .title` }),
  "outcomes.labels.won.subtitle": approved(4, "The board granted the reduction", { secondCopyOf: `${MODELS} .pickerSubtitle` }),
  "outcomes.labels.partiallyWon.title": approved(4, "Partially won", { secondCopyOf: `${MODELS} .title` }),
  "outcomes.labels.partiallyWon.subtitle": approved(4, "Some reduction, less than you requested", { secondCopyOf: `${MODELS} .pickerSubtitle` }),
  "outcomes.labels.denied.title": approved(4, "Denied", { secondCopyOf: `${MODELS} .title` }),
  "outcomes.labels.denied.subtitle": approved(4, "No reduction granted", { secondCopyOf: `${MODELS} .pickerSubtitle` }),
  "outcomes.labels.raised.title": approved(4, "Assessment raised", { secondCopyOf: `${MODELS} .title` }),
  "outcomes.labels.raised.subtitle": approved(4, "The board increased my assessment", { secondCopyOf: `${MODELS} .pickerSubtitle` }),
  "outcomes.labels.withdrawn.title": approved(4, "Withdrawn", { secondCopyOf: `${MODELS} .title` }),
  "outcomes.labels.withdrawn.subtitle": approved(4, "You dropped the appeal", { secondCopyOf: `${MODELS} .pickerSubtitle` }),
  "outcomes.sharingConsent": approved(
    5,
    "Include this result anonymously in PROppeal's stats. No address, parcel, or name is ever shared.",
    { secondCopyOf: "AppealAce/Features/Outcome/LogOutcomeView.swift (the Help other homeowners toggle)" },
  ),

  // ── the review screen: a deadline the tables could not verify ──
  // R189 change 4: the website promises no reminder until EM-3 exists. Item 6's place on the page, new words, its own
  // reply; the original (the app's ReminderService.swift fallback, which the app keeps) is in `REMINDER_ORIGINALS`.
  "deadline.unverified": approved(6, "Check your assessment notice for your county's appeal deadline.", {
    approval: LAUNCH_NO_REMINDERS,
  }),

  // ── any screen: the transport's own failures ──
  "transport.unreachable": approved(
    7,
    "We couldn't reach PROppeal's servers just now. Check your connection and try again in a moment.",
    { secondCopyOf: "AppealAce/Services/LetterService.swift (the unreachable arm)" },
  ),
  "transport.contractMismatch": approved(15, "This page is out of date. Refresh it to continue."),
  "transport.token": approved(
    16,
    "We couldn't verify this browser with PROppeal's servers. That's on our side, not yours — refresh the page, and if it keeps happening, contact support@proppeal.com.",
  ),

  // ── /checkout and /checkout/return ──
  // B6b (`B6B_SENTENCES_APPROVED`, 2026-10-09): item 8's words with the site's name corrected, and nothing else. No longer
  // a second copy of the 2026-09-28 purchase policies' Terms §6, which still says "proppeal.com": Terms v7 drops that item
  // for K2 (docs/legal/web-launch-2026-10/TERMS_V7.md, "Approved, but deliberately NOT in v7"), so these words are the
  // website's own.
  "checkout.separateBalances": approved(
    8,
    "At this time, credits bought in our iPhone app work only in the app, and credits bought on app.proppeal.com work only on the website. They don't move between the two.",
    { approval: B6B_SENTENCES_APPROVED },
  ),
  "checkout.testMode": approved(17, "Test mode: no real card is charged."),
  "checkout.returned": approved(
    18,
    "Thanks for your purchase. Your credit will appear here in a moment — if it doesn't, refresh the page.",
  ),

  // ── /delete-account ──
  "account.delete.unusedCredits": approved(
    9,
    "Deleting your account removes your credits, including unused ones, and we can't restore or refund them. Use them first if you plan to.",
    { secondCopyOf: `${POLICIES} (Refund Policy §9)` },
  ),
  "account.delete.confirm": approved(
    10,
    "This permanently deletes your account and any credits you haven't used. This cannot be undone.",
    { secondCopyOf: "AppealAce/Features/Account/AccountManagementView.swift (the delete alert)" },
  ),
  "account.delete.done": approved(19, "Your account has been deleted."),

  // ── /you: the letter's signature, and the two attestation FALLBACKS ──
  "letter.signature": approved(14, "Print your letter and sign it by hand before you file it."),
  "assent.adoptAsOwn": approved(
    11,
    "The requested value and the statements in this letter are my own. I've reviewed them, they're accurate to the best of my knowledge, and I am submitting this appeal as my own filing.",
    { secondCopyOf: `${ATTESTATIONS_V2} adoptAsOwn base`, pendingCounsel: COUNSEL_V2 },
  ),
  // The source bolds "raised" with Markdown asterisks; the approval page, and so this, shows the word plain.
  "assent.canRaiseAck": approved(
    12,
    "I understand that filing an appeal can result in my assessment being raised as well as lowered, and that PROppeal does not guarantee any outcome.",
    { secondCopyOf: `${ATTESTATIONS_V2} canRaiseAck base`, pendingCounsel: COUNSEL_V2 },
  ),
} as const satisfies Record<string, HeldSentence>;

export type HeldCopyId = keyof typeof HELD_COPY;

/** THE SHARING DEFAULT IS ON, AS THE APP'S IS (item 5: "on by default, as in the app"). A second copy of
 *  `@State private var shareAnonymously = true` in LogOutcomeView.swift, pinned there by `test/heldCopy.test.tsx`. No
 *  endpoint serves a default, so it is held with the sentence it belongs to. */
export const SHARING_DEFAULT = true;

// ── WEB-BENEFITS: /checkout's "What a credit gets you" card ─────────────────────────────────────────────────────────
//
// Tyler, 2026-09-29, on the trial checkout page: "the what a letter covers page needs to not have the number of
// iterations and revisions you can do.. It should be highlighting all the benefits of buying and running a credit".
// Shown this card's exact text, he answered "yes go aheaed.." the same day. The card replaced "What a credit covers",
// whose four rows printed the per-appeal caps; the SERVER still enforces those caps and still sends them. The card is
// held here, not served, for the last reason above: no endpoint serves it.
//
// A SEPARATE TABLE FROM WEB-COPY-1's, for the reason `functions/src/freecheck/copy.ts` keeps one approval constant per
// reply: the record shows which reply approved which sentence. These nine were never on WEB-COPY-1's numbered page, so
// they carry no page item; the card's order is `CREDIT_BENEFIT_ITEMS`.
//
// Where the words came from, per entry: a second copy (`secondCopyOf`), pinned word for word to its source by
// `test/heldCopy.test.tsx`; an adaptation (`adaptedFrom`), pinned to the sentence it was cut from with the cut named;
// or new wording (both null). The rule above holds here too: IF A PIN FAILS, DO NOT EDIT THE PIN.
//
// THE CARD IS NO LONGER ON `/checkout` (R251). Tyler, 2026-10-13: "i like the top part of this page
// but the bottom gets too long.. we need to encourage people to buy a credit and try it rather than scaring them away.. it
// gets too lengthy and the what a credit gets you kind of doubles down on the top part too.. mock up a new version". His
// words supersede the 2026-09-29 placement. Two of its lines still render, small, under the Buy button, in this order:
// `checkout.benefits.neverExpire` and `checkout.benefits.straightAnswer` (`routes/Checkout.tsx`). The table stays whole,
// approvals and pins unchanged; the heading, the lead and the other five items render nowhere now.

/** What Tyler said, and when. One reply approved the whole card, and a later one replaced item 6. */
export const WEB_BENEFITS = { by: "Tyler", on: "2026-09-29", quote: "yes go aheaed.." } as const;

/** ITEM 6, SETTLED THE SAME DAY: THE APP'S OWN SENTENCE, WORD FOR WORD. The first item 6 cut "and re-exporting" from the
 *  app's sentence because the website had no PDF. It was not true here either way: the website could neither edit an
 *  appeal's details nor re-export anything. CORRECTED THE SAME DAY (WEB-PARITY-AUDIT, measured in ReviewView.swift:157-161):
 *  the app has NO in-place edit of the letter text. Its letter row is display-only by design; the owner edits the
 *  APPEAL (details, comps, reasons) and rebuilds, asks for AI changes ("I need a change to this letter", 20 per appeal),
 *  re-exports the PDF as often as they like, or copies the text into Word or Pages. That is the sense in which the app's
 *  sentence is true. Tyler's answer was that the website does what the app does, AI rewrites included, so the card
 *  carries the app's sentence unchanged.
 *  LAUNCH CONDITION: true on the website only once it has the app's editable details, AI change requests, rebuild and PDF
 *  re-export (WEB-PDF done; WEB-LETTER-PARITY and the parity batches). `docs/WEB_READINESS.md` holds it as a launch
 *  blocker. WEB-PARITY-6 (R221) built the letter half: `/letter` is the app's Review & Export — AI
 *  change requests (20, the server's counter), "Revise from your details", re-export of the current and earlier PDFs,
 *  and, as in the app, no hand edit of the letter (D1). */
export const WEB_BENEFITS_ITEM_6 = {
  by: "Tyler",
  on: "2026-09-29",
  quote: "the site should replicate exactly what the app has at this time.. so AI rewrites",
} as const;

export interface BenefitSentence {
  readonly text: string;
  readonly status: "approved";
  readonly approval: typeof WEB_BENEFITS | typeof WEB_BENEFITS_ITEM_6;
  /** The file and symbol these words were first written in, word for word; null when they are not a copy. */
  readonly secondCopyOf: string | null;
  /** The sentence these words were cut from, and the cut; null when they are not an adaptation. */
  readonly adaptedFrom: { readonly source: string; readonly change: string } | null;
}

function benefit(
  text: string,
  from: {
    secondCopyOf?: string;
    adaptedFrom?: { source: string; change: string };
    approval?: typeof WEB_BENEFITS | typeof WEB_BENEFITS_ITEM_6;
  } = {},
): BenefitSentence {
  return {
    text,
    status: "approved",
    approval: from.approval ?? WEB_BENEFITS,
    secondCopyOf: from.secondCopyOf ?? null,
    adaptedFrom: from.adaptedFrom ?? null,
  };
}

const BUY_PACKETS = "AppealAce/Features/Purchase/BuyPacketsSheet.swift";

export const CREDIT_BENEFITS = {
  // New for this card.
  "checkout.benefits.heading": benefit("What a credit gets you"),
  // Tyler's free-check close (option B), approved for the free check on 2026-09-28 and for this card on 2026-09-29.
  "checkout.benefits.lead": benefit(
    "We find homes sold near you in the last year. Unlock them, your answer and your appeal letter.",
    { secondCopyOf: "functions/src/freecheck/copy.ts `freecheck.close.body`" },
  ),
  // The list, in the order Tyler approved it. Items 1, 2 and 4 are the app's shipped perks and, like them, end
  // without a period; the others end with one.
  "checkout.benefits.autofill": benefit("Auto-fill property details from public records", {
    secondCopyOf: `${BUY_PACKETS} \`perksCard\` (a shipped perk)`,
  }),
  "checkout.benefits.compSearch": benefit("Multi-source comp search — recent sales ranked to your home", {
    secondCopyOf: `${BUY_PACKETS} \`perksCard\` (a shipped perk)`,
  }),
  "checkout.benefits.straightAnswer": benefit(
    "A straight answer: does your assessment hold up? If it does, we tell you not to file.",
  ),
  "checkout.benefits.customLetter": benefit("PROppeal writes a custom letter citing your comps and photos", {
    secondCopyOf: `${BUY_PACKETS} \`perksCard\` (a shipped perk)`,
  }),
  "checkout.benefits.fileIt": benefit(
    "Copy your letter into your county's online portal, or print it and sign it by hand.",
  ),
  // The app's sentence, unchanged, in its own reply (WEB_BENEFITS_ITEM_6). True here once /letter matches the app.
  "checkout.benefits.editingFree": benefit("Editing and re-exporting stays free for the life of that appeal.", {
    secondCopyOf: `${BUY_PACKETS} \`hero\`, its second sentence`,
    approval: WEB_BENEFITS_ITEM_6,
  }),
  "checkout.benefits.neverExpire": benefit("Credits never expire. Use one whenever you're ready to file.", {
    secondCopyOf: `${BUY_PACKETS} \`finePrint\` (line 532, after its bullet), which matches the approved refund policy`,
  }),
} as const satisfies Record<string, BenefitSentence>;

export type CreditBenefitId = keyof typeof CREDIT_BENEFITS;

/** The card's list, in Tyler's order. The heading and the lead are the card's own, not items. */
export const CREDIT_BENEFIT_ITEMS = [
  "checkout.benefits.autofill",
  "checkout.benefits.compSearch",
  "checkout.benefits.straightAnswer",
  "checkout.benefits.customLetter",
  "checkout.benefits.fileIt",
  "checkout.benefits.editingFree",
  "checkout.benefits.neverExpire",
] as const satisfies readonly CreditBenefitId[];

// ── WEB-PARITY-1B: the two sentences the app has no words for (W-2, W-3) ─────────────────────────────────────────────
//
// The parity audit (`docs/WEB_APP_PARITY.md`, "Wording that is NOT in the app") drafted two sentences for places where
// the website showed developer text and the app shows nothing: a check that has not run (the not-run note printed
// field and endpoint names, SW-5) and input the server could not use (the dropped-input list printed raw
// `field — problem` lines, SW-6). Tyler approved both on 2026-09-29, replying "2. approve" to the orchestrator's
// question. Their own approval constant, as WEB_BENEFITS_ITEM_6 has its own: the record shows which reply approved which
// sentence. `test/heldCopy.test.tsx` pins each to the draft the audit wrote, word for word.

/** What Tyler said, and when. */
export const WEB_PARITY_1_COPY = { by: "Tyler", on: "2026-09-29", quote: "2. approve" } as const;

export interface ParitySentence {
  readonly text: string;
  readonly status: "approved";
  readonly approval: typeof WEB_PARITY_1_COPY;
  /** The audit's draft number (`docs/WEB_APP_PARITY.md`). */
  readonly draft: "W-2" | "W-3";
}

export const PARITY_SENTENCES = {
  // W-2: any decision that has not run yet (replaces the not-run note).
  "check.notRunYet": {
    text: "We haven't been able to check this yet. Try again in a moment.",
    status: "approved",
    approval: WEB_PARITY_1_COPY,
    draft: "W-2",
  },
  // W-3: part of the owner's input was not used (replaces the dropped-input list). `{field}` is the field's name in
  // plain words: the app's own label for it, from `FIELD_WORDS` below.
  "input.partlyUnused": {
    text: "We couldn't use part of what you entered: {field}. Everything else was saved.",
    status: "approved",
    approval: WEB_PARITY_1_COPY,
    draft: "W-3",
  },
} as const satisfies Record<string, ParitySentence>;

export type ParitySentenceId = keyof typeof PARITY_SENTENCES;

// ── WEB-PARITY-1B: the app's own words, used where the website printed developer text ───────────────────────────────
//
// Tyler, 2026-09-29: "the site should replicate exactly what the app has at this time". Where the website showed a code,
// an enum or a timestamp and the app shows words, the website now shows the app's words — unchanged, each with the
// file and line it was copied from (under `AppealAce/`). They are not new sentences, so they carry no new approval: the
// `approval` is Tyler's standing instruction, and `test/heldCopy.test.tsx` reads each cited line and fails when the
// app's words and these differ. A `{name}` is the app's own interpolation, written the web's way (`\(status)` →
// `{status}`); nothing else is changed.

/** Tyler's standing instruction for the parity batches, the same words WEB_BENEFITS_ITEM_6 records. */
export const WEB_PARITY = {
  by: "Tyler",
  on: "2026-09-29",
  quote: "the site should replicate exactly what the app has at this time",
} as const;

export interface AppWording {
  readonly text: string;
  readonly status: "appWording";
  readonly approval: typeof WEB_PARITY;
  /** `File.swift:line` under `AppealAce/`, where these words are the app's. */
  readonly appSource: string;
}

function app(appSource: string, text: string): AppWording {
  return { text, status: "appWording", approval: WEB_PARITY, appSource };
}

export const APP_WORDING = {
  // LAUNCH-PF: the pre-spend purchase notice's safe path, the app's filled button (PurchaseNoticeSheet.swift).
  "purchaseNotice.goBack": app("Features/Flow/PurchaseNoticeSheet.swift:71", "Go back"),
  // The rail's "Step n of N" (SW-3 in part).
  "steps.count": app("Features/Flow/AppealProgressBar.swift:86", "Step {n} of {total}"),
  // A server error with its status, where the refusal printed `CODE · status` (SW-7).
  "refusal.serverError": app(
    "Services/LetterService.swift:259",
    "Something went wrong on our end ({status}). Nothing was charged for a failed request. Try again in a moment — if it keeps happening, contact us and quote that number.",
  ),
  // The guard page for an appeal with no credit on it, where it spoke of "Entitlement" (SW-8).
  "guard.noCredit": app(
    "Services/LetterService.swift:227",
    "This appeal doesn't have a credit applied yet. Add a credit and try again.",
  ),
  // Loading lines, where they spoke of "the server" (SW-10).
  "loading.appeal": app("Features/Generate/LetterGenerationOverlay.swift:391", "Reviewing your appeal…"),
  "loading.record": app("Features/Subject/SubjectPropertyView.swift:1308", "Looking up your property in public records…"),
  "loading.comps": app("Features/Comps/CompsView.swift:313", "Searching nearby sales…"),
  "loading.compsReread": app("Features/Reasons/AnalyzeView.swift:294", "Reading your comparable sales…"),
  "building.subtitle": app("Features/Generate/GenerateView.swift:1576", "PROppeal is weighing every detail of your case."),
  // `/building`'s failure routes, where the button printed the route enum (BL-3). `RefusalRoute.title` and `.detail`.
  "letter.routes.heading": app("Features/Generate/GenerateView.swift:988", "Where the fix lives:"),
  "letter.routes.comps.title": app("Services/LetterService.swift:144", "I know of a recent sale nearby"),
  "letter.routes.condition.title": app("Services/LetterService.swift:145", "There's something wrong with my home"),
  "letter.routes.value.title": app("Services/LetterService.swift:146", "The county's record is wrong"),
  "letter.routes.comps.detail": app("Services/LetterService.swift:154", "Add a sale we missed and we'll fold it into the comparison."),
  "letter.routes.condition.detail": app(
    "Services/LetterService.swift:155",
    "Damage, deferred repairs, a defect — with photos, which carry far more weight than a description on its own.",
  ),
  "letter.routes.value.detail": app(
    "Services/LetterService.swift:156",
    "Wrong square footage, bedroom count, or anything else the county has on record for your home.",
  ),
  // `/home`'s badge, where it printed the appeal's status enum (HM-1). An outcome, when logged, shows item 4's title.
  "status.draft": app("Shared/StatusBadge.swift:23", "Draft"),
  "status.generated": app("Shared/StatusBadge.swift:24", "Generated"),
} as const satisfies Record<string, AppWording>;

export type AppWordingId = keyof typeof APP_WORDING;

/**
 * W-3's `{field}`, in plain words: THE APP'S OWN LABEL for the field the owner filled in, keyed by the request path the
 * server names on `degraded[]` (array indexes written `[]`). A path is matched by its longest mapped prefix, so
 * `ownerComps[].salePrice.amount` is "Sale price". A path with no label here is not something the owner entered — the
 * engine's own gaps, like `condition.deduction` or a comp's unreadable sale date — and the app says nothing about those,
 * so neither does the website (`DegradedList`).
 */
export const FIELD_WORDS = {
  "corrections.sqft": app("Features/Subject/SubjectPropertyView.swift:2351", "Square footage"),
  "corrections.beds": app("Features/Subject/SubjectPropertyView.swift:2381", "Bedrooms"),
  "corrections.baths": app("Features/Subject/SubjectPropertyView.swift:2390", "Bathrooms"),
  "corrections.assessedValue": app("Features/Subject/SubjectPropertyView.swift:1775", "Assessed value"),
  "corrections.parcel": app("Features/Subject/SubjectPropertyView.swift:1716", "Parcel ID / APN"),
  "corrections.propertyType": app("Features/Subject/SubjectPropertyView.swift:2300", "Property type"),
  "corrections.yearBuilt": app("Features/Subject/SubjectPropertyView.swift:2410", "Year built"),
  "corrections.lotSizeSqft": app("Features/Subject/SubjectPropertyView.swift:2407", "Lot size"),
  propertyType: app("Features/Subject/SubjectPropertyView.swift:2300", "Property type"),
  conditionNote: app("Features/Reasons/CaseEvidenceCards.swift:50", "Condition & damage"),
  "photos[]": app("Features/Reasons/CaseEvidenceCards.swift:105", "Photo evidence"),
  "ownerComps[].address": app("Features/Comps/CompEditView.swift:142", "Address"),
  "ownerComps[].salePrice": app("Features/Comps/CompEditView.swift:143", "Sale price"),
  "ownerComps[].saleDate": app("Features/Comps/CompEditView.swift:146", "Sale date"),
  "ownerComps[].sqft": app("Features/Comps/CompEditView.swift:179", "Square footage"),
  "ownerComps[].beds": app("Features/Comps/CompEditView.swift:191", "Bedrooms"),
} as const satisfies Record<string, AppWording>;

/** The app's label for a `degraded[]` field path, or null when the owner did not enter it (see `FIELD_WORDS`). */
export function fieldWords(path: string): string | null {
  const segments = path.replace(/\[[^\]]*\]/g, "[]").split(".");
  for (let n = segments.length; n > 0; n--) {
    const key = segments.slice(0, n).join(".");
    if (Object.hasOwn(FIELD_WORDS, key)) return FIELD_WORDS[key as keyof typeof FIELD_WORDS].text;
  }
  return null;
}

// ── WEB-ACC-3a: the website's account screens (2026-09-30) ──────────────────────────────────────────────────────────
//
// Tyler, 2026-09-30, on the one-account review page (https://claude.ai/artifact/YBVVNDgaeYmFwvxZfBszU8, source: the
// orchestrator's ACCOUNTS_PLAN.md §3): "Sentences approved" — A0 to L7 and M1 to M3, recorded in
// `docs/legal/WEB_PURCHASE_POLICIES.md` (`e6ae17df`, `8d3cacca`). THESE ARE THE ONES THE WEBSITE'S ACCOUNT SCREENS SAY:
// A0–A11, B0–B9, C1–C12, D1–D5, F1–F5 and K7 (A12's small print) — and, since ACC-3b (2026-10-01), G1–G10, J3, K1, K5,
// K6, K12 and L7, from the same reply. Their own table and their own approval constant, as WEB_BENEFITS has, because a
// different reply approved them; each carries its number on that page (`planId`).
//
// THREE ARE ALREADY HELD, AND ARE NOT HELD TWICE: A7 is WEB-COPY-1 item 2 (`assent.standingDisclosure`), A8 is item 1
// (`assent.onboardingCheckbox`, its counsel marker kept), and C9 is item 7 (`transport.unreachable`) — the plan cites each
// as APPROVED there. A row that is a second copy of the app's words or of the server's table names its source
// (`secondCopyOf`), and `test/heldCopy.test.tsx` section 7 reads that source; `test/accountsPlan.ts` is the page's own
// text, and `test/accountScreens.test.tsx` compares what renders against it. C12 and F5 are not on a website screen (C12
// is the app's; F5 is a reminder email's) and are held so the record is whole; C11 shows on ACC-3b's "Add Apple".
//
// A `{name}` is filled by the page. A link inside a sentence is found in it by range lookup (`linkPhrase`), as the assent
// page finds its two, so the link can never name words the sentence does not contain.
// IF A PIN FAILS, DO NOT EDIT THE PIN.

/** What Tyler said, and when. One reply approved the whole plan's wording. */
export const WEB_ACC_3A = { by: "Tyler", on: "2026-09-30", quote: "Sentences approved" } as const;

export interface AccountSentence {
  readonly text: string;
  readonly status: "approved";
  /** WEB-ACC-3a's reply — or, for A5 and A6, R189's (`LAUNCH_NO_REMINDERS`), which replaced their words for launch; for
   *  D4, G10's last line, K7, B9, K1, K5, K6 and K12, B6b's (`B6B_SENTENCES_APPROVED`, 2026-10-09). */
  readonly approval: typeof WEB_ACC_3A | typeof LAUNCH_NO_REMINDERS | typeof B6B_SENTENCES_APPROVED;
  /** The plan's number for these words (ACCOUNTS_PLAN.md §3): "A0", "C7", "K7". */
  readonly planId: string;
  /** The file and symbol (or server copy code) these words were first written in, when a second copy; null when new. */
  readonly secondCopyOf: string | null;
  /** The words inside `text` that are a link, when some are. */
  readonly linkPhrase: string | null;
}

function acc(
  planId: string,
  text: string,
  from: { secondCopyOf?: string; linkPhrase?: string; approval?: typeof LAUNCH_NO_REMINDERS | typeof B6B_SENTENCES_APPROVED } = {},
): AccountSentence {
  return {
    text,
    status: "approved",
    approval: from.approval ?? WEB_ACC_3A,
    planId,
    secondCopyOf: from.secondCopyOf ?? null,
    linkPhrase: from.linkPhrase ?? null,
  };
}

const ACCOUNT_VIEW = "AppealAce/Features/Account/AccountManagementView.swift";
const PROFILE_TAB = "AppealAce/Features/Tabs/ProfileTab.swift";
const SERVER_COPY = "functions/src/v2/copy/table.ts";

export const ACCOUNT_COPY = {
  // ── A. Create your account (`/account`, with the server's WEB_ACCOUNTS_ENABLED on) ──
  "account.create.apple": acc("A0", "Continue with Apple"),
  "account.create.heading": acc("A1", "Create your account"),
  "account.create.tied": acc("A2", "Your credits and saved appeals are tied to this account.", { secondCopyOf: `${ACCOUNT_VIEW}:38` }),
  "account.create.divider": acc("A3", "or use your email"),
  "account.field.email": acc("A3", "Email", { secondCopyOf: "AppealAce/Features/Tabs/OwnerProfileEditor.swift:79" }),
  "account.field.emailPlaceholder": acc("A3", "you@example.com", { secondCopyOf: "AppealAce/Features/Review/EmailCopyRow.swift:112" }),
  "account.field.password": acc("A4", "Password"),
  "account.field.passwordHint": acc("A4", "At least 8 characters."),
  "account.field.show": acc("A4", "Show"),
  "account.field.hide": acc("A4", "Hide"),
  // R189 changes 1 and 2 (`LAUNCH_NO_REMINDERS`): no reminder is promised until EM-3 exists. A6 keeps "next year's
  // deadline", as the app's approved R175 sentence does. Neither asks for a reminders consent, so the sign-up records
  // none (CreateAccount.tsx); the originals, which the server still issues as reminders wording, are `REMINDER_ORIGINALS`.
  "account.create.emailHelp": acc(
    "A5",
    "You'll sign in with this email. We'll also use it for messages about your account. Nothing else.",
    { approval: LAUNCH_NO_REMINDERS },
  ),
  "account.create.emailHelpWithNews": acc(
    "A6",
    "You'll sign in with this email. We'll also use it for messages about your account, to tell you when next year's deadline is coming, and to send news and offers. Nothing else.",
    { approval: LAUNCH_NO_REMINDERS },
  ),
  "account.create.marketing": acc(
    "A9",
    "Also email me when next year's deadline is coming, and send me news and offers from PROppeal. Unsubscribe any time.",
  ),
  "account.create.button": acc("A10", "Create account and continue"),
  "account.create.haveAccount": acc("A11", "Already have an account? Sign in", { linkPhrase: "Sign in" }),
  // B6b (2026-10-09): K7, B9, D4, G10's last line and K1 stop promising the app's email sign-in, which ships after the
  // website (R189 answer 2) — Apple works in both places, email and password on the website.
  "account.create.appNote": acc(
    "K7",
    "Use the PROppeal iPhone app? Continue with Apple on app.proppeal.com and in the app, and your credits come with you.",
    { approval: B6B_SENTENCES_APPROVED },
  ),

  // ── B. Sign in (`/signin`) ──
  "account.signIn.apple": acc("B0", "Continue with Apple"),
  "account.signIn.heading": acc("B1", "Sign in", { secondCopyOf: `${PROFILE_TAB}:184` }),
  "account.signIn.lead": acc("B2", "Sign in or create an account to save your credits", { secondCopyOf: `${PROFILE_TAB}:178` }),
  "account.signIn.button": acc("B4", "Sign in", { secondCopyOf: `${PROFILE_TAB}:184` }),
  "account.signIn.forgot": acc("B5", "Forgot your password?"),
  "account.signIn.newHere": acc("B6", "New to PROppeal? Create an account", { linkPhrase: "Create an account" }),
  "account.signIn.lookAround": acc("B7", "We'll check this address again under your account. It only takes a moment."),
  "account.signIn.appleInApp": acc("B8", "Use Sign in with Apple in the PROppeal app? Use it here too, and your credits come with you."),
  "account.signIn.appCredits": acc(
    "B9",
    "Bought credits in the app without signing in? Open the app, sign in with Apple from Settings, then continue with Apple on app.proppeal.com.",
    { approval: B6B_SENTENCES_APPROVED },
  ),

  // ── C. Errors (C9 is WEB-COPY-1 item 7, `transport.unreachable`) ──
  "account.error.noMatch": acc("C1", "That email and password don't match an account. Check them and try again, or reset your password."),
  "account.error.emailHasAccount": acc(
    "C2",
    "There's already an account with this email. Sign in instead — with Apple, if that's how you signed up in the app — or reset your password.",
  ),
  "account.error.tooManyTries": acc("C3", "Too many tries. Wait a few minutes and try again, or reset your password.", {
    secondCopyOf: `${SERVER_COPY} \`account.email.tooManyTries\``,
  }),
  "account.error.passwordTooShort": acc("C4", "Use at least 8 characters."),
  "account.error.notAnEmail": acc("C5", "Enter an email address like you@example.com.", {
    secondCopyOf: `${SERVER_COPY} \`account.email.notAnEmail\``,
  }),
  "account.error.reauthPassword": acc("C6", "For security, enter your password to continue."),
  "account.error.reauthApple": acc("C6", "For security, sign in with Apple again to continue."),
  "account.confirm.heading": acc("C7", "Confirm your email to continue", {
    secondCopyOf: `${SERVER_COPY} \`checkout.emailNotVerified\``,
  }),
  "account.confirm.body": acc("C7", "We sent a link to {email}. Open it, then come back to this page."),
  "account.confirm.resend": acc("C7", "Send it again"),
  "account.confirm.changeEmail": acc("C7", "Change email"),
  "account.confirm.sent": acc("C7", "Sent. If it isn't there in a few minutes, check your spam folder."),
  "account.error.accountRequired": acc("C8", "Create an account or sign in to buy credits. Nothing was charged.", {
    secondCopyOf: `${SERVER_COPY} \`checkout.accountRequired\``,
  }),
  "account.error.emailHasPasswordAccount": acc(
    "C10",
    "You already have a PROppeal account with this email. Sign in with your email and password, then add Apple in your account settings.",
  ),
  "account.error.appleIdIsAnotherAccount": acc(
    "C11",
    "This Apple ID is already signed in to a different PROppeal account. Email support@proppeal.com and we'll combine them for you.",
  ),
  "account.error.purchaseOnAnotherAccount": acc(
    "C12",
    "This purchase is already on a different PROppeal account. Email support@proppeal.com and we'll sort it out.",
  ),

  // ── D. Forgot password (`/forgot`, and `/account/action` for the link) ──
  "account.forgot.heading": acc("D1", "Reset your password"),
  "account.forgot.body": acc("D1", "Enter your account email and we'll send you a link to choose a new password."),
  "account.forgot.button": acc("D1", "Send link"),
  "account.forgot.sent": acc(
    "D2",
    "If an account uses {email}, we've sent a link to it. If it isn't there in a few minutes, check your spam folder.",
  ),
  "account.reset.heading": acc("D3", "Choose a new password"),
  "account.reset.field": acc("D3", "New password"),
  "account.reset.button": acc("D3", "Save password"),
  "account.reset.done": acc("D4", "Your password is changed. Sign in with your new password on app.proppeal.com.", {
    approval: B6B_SENTENCES_APPROVED,
  }),
  "account.link.expired": acc("D5", "This link has expired or was already used. Ask for a new one."),

  // ── F. Signed-out states and the reminder link ──
  "account.chip.signIn": acc("F1", "Sign in", { secondCopyOf: `${PROFILE_TAB}:184` }),
  "account.chip.account": acc("F1", "Account", { secondCopyOf: `${ACCOUNT_VIEW}:97` }),
  "account.signedOut.appeals": acc("F2", "Sign in to see your appeals and credits."),
  "account.signedOut.paid": acc("F3", "You're signed out. Sign in to pick up where you left off."),
  "account.signedOut.done": acc("F4", "You're signed out."),
  "account.reminder.openAppeal": acc("F5", "Sign in to your PROppeal account to open your appeal."),

  // ── WEB-ACC-3b (2026-10-01): the same reply's G, J3, K and L7 rows, which ACC-3b's screens say ──────────────────────
  // G. Account settings (`/account/settings`). G6 is A9, held once above; G9's footer is K12.
  "account.settings.heading": acc("G1", "Account", { secondCopyOf: `${ACCOUNT_VIEW}:97` }),
  "account.settings.tied": acc("G1", "Your credits and saved appeals are tied to this account.", { secondCopyOf: `${ACCOUNT_VIEW}:38` }),
  "account.settings.email": acc("G2", "Email"),
  "account.settings.changeEmail": acc("G2", "Change email"),
  "account.settings.changeEmailHelp": acc(
    "G3",
    "Enter your new email. We'll send a link to it, and the change happens when you open the link.",
  ),
  "account.settings.changeEmailSent": acc("G3", "Check {newEmail} for a link to confirm the change."),
  "account.settings.password": acc("G4", "Password"),
  "account.settings.changePassword": acc("G4", "Change password"),
  "account.settings.currentPassword": acc("G4", "Current password"),
  "account.settings.newPassword": acc("G4", "New password"),
  "account.settings.passwordChanged": acc("G4", "Your password is changed."),
  // G5: HELD, NOT RENDERED (R189 change 3) — the settings page shows its reminders row only while WEBSITE_REMINDERS_LIVE.
  "account.settings.reminders": acc("G5", "Email me reminders about my appeals"),
  "account.settings.remindersHelp": acc("G5", "Before your filing deadline, and when it's time to log the county's decision."),
  "account.settings.signOut": acc("G7", "Sign out", { secondCopyOf: `${ACCOUNT_VIEW}:46` }),
  "account.settings.signOutFooter": acc(
    "G7",
    "You'll need to sign back in to access your credits — they stay safely on your account.",
    { secondCopyOf: `${ACCOUNT_VIEW}:49` },
  ),
  "account.settings.signOutConfirm": acc("G7", "Sign out of PROppeal?", { secondCopyOf: `${ACCOUNT_VIEW}:100` }),
  "account.settings.signOutEverywhere": acc("G8", "Sign out on all devices"),
  "account.settings.signOutEverywhereHelp": acc("G8", "Signs you out everywhere, including this browser and the app."),
  "account.settings.delete": acc("G9", "Delete account", { secondCopyOf: `${ACCOUNT_VIEW}:73` }),
  "account.settings.ways": acc("G10", "Ways to sign in"),
  "account.settings.ways.apple": acc("G10", "Apple ✓"),
  "account.settings.ways.addApple": acc("G10", "Add Apple"),
  "account.settings.ways.password": acc("G10", "Email and password ✓"),
  "account.settings.ways.addPassword": acc("G10", "Add a password"),
  "account.settings.ways.anywhere": acc(
    "G10",
    "Apple works in the PROppeal app and on app.proppeal.com. Email and password work on app.proppeal.com.",
    { approval: B6B_SENTENCES_APPROVED },
  ),
  // J3 — `/outcome`, before saving, a letter shipped and no offer waiting. (J1 is the app's own line, held with the
  // outcome screen's app words, `afterFilingCopy.ts`; J2 is the checkout's "Welcome back" card, `prePayCopy.ts`, 2B.)
  "account.returning.beforeDecision": acc(
    "J3",
    "When the county decides, log it here and your next appeal is at the returning-owner price.",
  ),
  // K. The replacements for sentences one account makes false — shown with the switch on, in place of WEB-COPY-1's items 8,
  // 9 and 10 and the app's delete footer, which stay exactly as they are with it off (today's site). K1 is B6b's Apple-only
  // wording; K5, K6 and K12 are their approved words with the site's name corrected and nothing else (2026-10-09).
  "account.k.creditsBothPlaces": acc(
    "K1",
    "Your credits work on app.proppeal.com, and in our iPhone app when you sign in there with Apple to this same account, wherever you bought them.",
    { approval: B6B_SENTENCES_APPROVED },
  ),
  "account.k.deleteRemovesEverywhere": acc(
    "K5",
    "Deleting your account removes your credits everywhere — in our iPhone app and on app.proppeal.com — including unused ones, and we can't restore or refund them. Use them first if you plan to.",
    { approval: B6B_SENTENCES_APPROVED },
  ),
  "account.k.deleteConfirm": acc(
    "K6",
    "This permanently deletes your account everywhere — in our iPhone app and on app.proppeal.com — with any credits you haven't used. This cannot be undone.",
    { approval: B6B_SENTENCES_APPROVED },
  ),
  "account.k.deleteFooter": acc(
    "K12",
    "Permanently deletes your account and any remaining credits, in the app and on app.proppeal.com. This can't be undone.",
    { approval: B6B_SENTENCES_APPROVED },
  ),
  // L7 — `/home`'s footer for an account.
  "account.l7.appAppeals": acc("L7", "Appeals you started in the PROppeal iPhone app stay in the app."),
} as const satisfies Record<string, AccountSentence>;

export type AccountCopyId = keyof typeof ACCOUNT_COPY;

/** The plan numbers the website holds, and the three it reuses from WEB-COPY-1 (A7, A8, C9) — for the record's test. */
export const ACCOUNT_PLAN_REUSED = {
  A7: "assent.standingDisclosure",
  A8: "assent.onboardingCheckbox",
  C9: "transport.unreachable",
} as const satisfies Record<string, HeldCopyId>;

/**
 * THE THREE SENTENCES R189 REPLACED, WORD FOR WORD AS FIRST APPROVED — KEPT, NEVER RENDERED. They return with EM-3 (the
 * reminder job), and each is still pinned where it lives: A5's and A6's originals are the reminders wording the server
 * issues (functions/src/v2/email/wording.ts `CONSENT_WORDING`), and `deadline.unverified`'s is the app's own fallback
 * (ReminderService.swift), which the app keeps. Not part of `AnyHeldId`, so `heldText` cannot reach them.
 */
export const REMINDER_ORIGINALS = {
  "account.create.emailHelp": {
    text: "You'll sign in with this email. We'll also use it for messages about your account and to remind you about this appeal: before your filing deadline, and when it's time to log the county's decision. Nothing else.",
    approval: WEB_ACC_3A, planId: "A5", returnsWith: "EM-3",
  },
  "account.create.emailHelpWithNews": {
    text: "You'll sign in with this email. We'll also use it for messages about your account, to remind you about this appeal, to tell you when next year's deadline is coming, and to send news and offers. Nothing else.",
    approval: WEB_ACC_3A, planId: "A6", returnsWith: "EM-3",
  },
  "deadline.unverified": {
    text: "Add your county's appeal deadline from your notice so we can remind you before it closes.",
    approval: { ...WEB_COPY_1, item: 6 }, secondCopyOf: "AppealAce/Services/ReminderService.swift (the no-deadline fallback)",
    returnsWith: "EM-3",
  },
} as const;

// ── B6b (R189): THE DRAFTS ARE THE WORDS NOW (approved 2026-10-09) ──────────────────────────────────────────────────────
//
// B6b drafted ten sentences and held them here, never rendered, as `APPLE_ONLY_DRAFTS` (D4, G10, K7, B9, K1: the app signs
// in with Apple only until its email sign-in ships) and `SITE_NAME_DRAFTS` (item 8, K5, K6, K12 and the pop-up line: the
// paid website is `app.proppeal.com`, never the marketing site's bare `proppeal.com`). Tyler approved all ten word for word
// (`B6B_SENTENCES_APPROVED`), so each draft became its entry's text — above, and the pop-up line's below — and both
// tables are gone: a held draft beside the words it replaced would be a second writer for one sentence. The record of
// old beside new is docs/WEB_COPY_TODO.md (B6b), held to this file by `test/appleOnlyDrafts.test.ts`, whose ratchet now allows bare
// `proppeal.com` in none of this client's code but the free check's own link (`app/handIn.ts` `FREE_CHECK_URL`).

// ── F-ACC3a-2: the three account sentences ACC-3a left as drafts, approved 2026-10-01 ───────────────────────────────────
//
// ACC-3a found three places with no approved sentence and shipped none (docs/WEB_READINESS.md, ACC-3a, F-ACC3a-2): the
// page after a confirm-email link opens, Apple's window blocked by the browser, and a turned-off account — the last two
// showed C9, the fallback every unmapped refusal shows. Tyler, 2026-10-01, on the three drafts: "yes those sentences look
// good. Fix the test" (recorded on main at `e7aaae14`, docs/legal/WEB_PURCHASE_POLICIES.md, "Three website account
// sentences ACC-3a left as drafts"). Their own table and constant: a different reply approved them. Shown only on the
// account screens, which exist only with the server's WEB_ACCOUNTS_ENABLED on. IF A PIN FAILS, DO NOT EDIT THE PIN.

/** What Tyler said, and when. */
export const ACC_3A_DRAFTS_APPROVED = { by: "Tyler", on: "2026-10-01", quote: "yes those sentences look good" } as const;

export interface AccountFollowUpSentence {
  readonly text: string;
  readonly status: "approved";
  /** F-ACC3a-2's reply — or, for the pop-up line, B6b's (`B6B_SENTENCES_APPROVED`, 2026-10-09), which corrected the site. */
  readonly approval: typeof ACC_3A_DRAFTS_APPROVED | typeof B6B_SENTENCES_APPROVED;
  /** The finding that drafted it. */
  readonly finding: "F-ACC3a-2";
}

function followUp(
  text: string,
  approval: typeof ACC_3A_DRAFTS_APPROVED | typeof B6B_SENTENCES_APPROVED = ACC_3A_DRAFTS_APPROVED,
): AccountFollowUpSentence {
  return { text, status: "approved", approval, finding: "F-ACC3a-2" };
}

export const ACCOUNT_FOLLOW_UP_COPY = {
  "account.confirm.done": followUp("Your email is confirmed."),
  // B6b (2026-10-09): the site the owner allows pop-ups for is the paid website, `app.proppeal.com` — the 2026-10-01 words
  // named the marketing site, where no Apple window ever opens. Only the name changed.
  "account.error.popupBlocked": followUp(
    "Your browser blocked the Apple window. Allow pop-ups for app.proppeal.com and try again.",
    B6B_SENTENCES_APPROVED,
  ),
  "account.error.accountDisabled": followUp("This account is turned off. Email support@proppeal.com and we'll help."),
} as const satisfies Record<string, AccountFollowUpSentence>;

export type AccountFollowUpId = keyof typeof ACCOUNT_FOLLOW_UP_COPY;

// ── ACC-3b: the line under "Add a password", approved 2026-10-01 ───────────────────────────────────────────────────────
//
// An Apple account that adds a password signs in with it under the account's own email — which may be Apple's relay
// address, not one the owner ever typed. ACC-3b drafted a line saying which address the password goes with; Tyler,
// 2026-10-01: "sign in sentence is good". Its own constant: a different reply approved it. Shown only on the account's
// settings page, which exists only with the server's WEB_ACCOUNTS_ENABLED on. IF A PIN FAILS, DO NOT EDIT THE PIN.

/** What Tyler said, and when. */
export const ADD_PASSWORD_LINE_APPROVED = { by: "Tyler", on: "2026-10-01", quote: "sign in sentence is good" } as const;

export interface AddPasswordSentence {
  readonly text: string;
  readonly status: "approved";
  readonly approval: typeof ADD_PASSWORD_LINE_APPROVED;
}

export const ADD_PASSWORD_COPY = {
  "account.settings.ways.addPasswordEmail": {
    text: "You'll sign in with {email} and this password.",
    status: "approved",
    approval: ADD_PASSWORD_LINE_APPROVED,
  },
} as const satisfies Record<string, AddPasswordSentence>;

export type AddPasswordId = keyof typeof ADD_PASSWORD_COPY;

// ── THE EMAIL-CONFIRMED PAGE, approved 2026-10-14 (launch day) ─────────────────────────────────────────────────────────
//
// A confirm link opened in a tab that holds no appeal used to land on a bare "Your email is confirmed." note. Tyler, on
// production the day the website launched: "This page just looks unfinished/not professional.. mock up a new one". Offered
// two mockups and these words; his reply, "A but check my property should be in the orange font" (the button is orange
// text on an orange outline, `.btn.amber-outline`), then "Great". The body names the address only when the session
// knows it (`session.auth.email`); without one the page says the 2026-10-01 sentence, `account.confirm.done`. A tab that
// holds an appeal still goes to /checkout with that sentence. Pinned word for word by `test/heldCopy.test.tsx`, section 13.
// IF A PIN FAILS, DO NOT EDIT THE PIN.

/** What Tyler said, and when. */
export const CONFIRMED_PAGE_APPROVED = {
  by: "Tyler", on: "2026-10-14", quote: "A but check my property should be in the orange font",
} as const;

export interface ConfirmedPageSentence {
  readonly text: string;
  readonly status: "approved";
  readonly approval: typeof CONFIRMED_PAGE_APPROVED;
}

const confirmed = (text: string): ConfirmedPageSentence => ({ text, status: "approved", approval: CONFIRMED_PAGE_APPROVED });

export const CONFIRMED_PAGE_COPY = {
  "account.confirmed.heading": confirmed("You're all set"),
  "account.confirmed.body": confirmed("Your email {email} is confirmed. Your PROppeal account is ready."),
  "account.confirmed.next": confirmed("Check my property"),
  "account.confirmed.otherTab": confirmed("Started in another tab? You can close this one and pick up where you left off."),
} as const satisfies Record<string, ConfirmedPageSentence>;

export type ConfirmedPageId = keyof typeof CONFIRMED_PAGE_COPY;

// ── `/checkout`'S CREDITS LINE FOR A SIGNED-IN ACCOUNT, approved 2026-10-14 (launch day) ────────────────────────────────
//
// With web accounts on, `/checkout` said K1 (`account.k.creditsBothPlaces`) to every signed-in account. Tyler, buying on
// production the day the website launched, found it confusing, and for an email-and-password account it is wrong: the
// iPhone app signs in only with Apple (R189 answer 2). He chose option B, two sentences picked by whether the account has
// Apple linked (`routes/balanceLine.ts`); his reply, "B.. im going to keep going". The second names the Account page's
// "Add Apple" (G10, `account.settings.ways.addApple`), which a password account is offered there. K1 stays in
// ACCOUNT_COPY as the record and renders nowhere. Pinned word for word by `test/heldCopy.test.tsx`, section 14.
// IF A PIN FAILS, DO NOT EDIT THE PIN.

/** What Tyler said, and when. */
export const CHECKOUT_CREDITS_APPROVED = { by: "Tyler", on: "2026-10-14", quote: "B.. im going to keep going" } as const;

export interface CheckoutCreditsSentence {
  readonly text: string;
  readonly status: "approved";
  readonly approval: typeof CHECKOUT_CREDITS_APPROVED;
}

const creditsLine = (text: string): CheckoutCreditsSentence => ({ text, status: "approved", approval: CHECKOUT_CREDITS_APPROVED });

export const CHECKOUT_CREDITS_COPY = {
  // An account with Apple linked: the app signs in to the same account, so the same balance.
  "checkout.credits.appleLinked": creditsLine(
    "Your credits are saved to your account and also work in our iPhone app when you sign in there with Apple.",
  ),
  // An email-and-password account without Apple: the app cannot reach it until Apple is added under Account.
  "checkout.credits.noApple": creditsLine(
    "Your credits are saved to your account. To use them in our iPhone app too, add Apple under Account, then sign in there with Apple.",
  ),
} as const satisfies Record<string, CheckoutCreditsSentence>;

export type CheckoutCreditsId = keyof typeof CHECKOUT_CREDITS_COPY;

// ── THE WALK'S DRAFTS B AND C, approved 2026-10-05 ─────────────────────────────────────────────────────────────────────
//
// Tyler's test-site walk left drafts on two batches: B, the no-case result's "what your credit did" panel (WEB-DESIGN-1,
// `routes/steps/CreditWork.tsx`), and C, the unsigned letter (WALK-FIX-1, `routes/steps/Letter.tsx`). Offered word for
// word and approved in one reply, "i like the background with the homes. Yes ABC, yes put on test site" (recorded on main
// at `344c1323`, docs/legal/WEB_PURCHASE_POLICIES.md, "The walk's drafts approved, and the background chosen
// (2026-10-05)"). A is the app's, on another branch. Pinned word for word by `test/heldCopy.test.tsx`, section 10.
// IF A PIN FAILS, DO NOT EDIT THE PIN.

/** What Tyler said, and when. */
export const WALK_DRAFTS_APPROVED = { by: "Tyler", on: "2026-10-05", quote: "Yes ABC, yes put on test site" } as const;

export interface WalkSentence {
  readonly text: string;
  readonly status: "approved";
  readonly approval: typeof WALK_DRAFTS_APPROVED;
  /** The record's letter for it, and the batch that drafted it. */
  readonly draft: "B" | "C";
  readonly batch: "WEB-DESIGN-1" | "WALK-FIX-1";
}

function walkB(text: string): WalkSentence {
  return { text, status: "approved", approval: WALK_DRAFTS_APPROVED, draft: "B", batch: "WEB-DESIGN-1" };
}

function walkC(text: string): WalkSentence {
  return { text, status: "approved", approval: WALK_DRAFTS_APPROVED, draft: "C", batch: "WALK-FIX-1" };
}

export const WALK_COPY = {
  "work.heading": walkB("What your credit did"),
  "work.sales": walkB("Sales found and compared to your home"),
  "work.record": walkB("County record checked"),
  "work.history": walkB("Sale history checked"),
  "work.check": walkB("Case check run"),
  "work.saved": walkB("Saved to your account"),
  "review.unsignedCaption": walkC("Not signed yet"),
  "review.export.unsignedNote": walkC("Your letter will print with a blank line for you to sign by hand."),
} as const satisfies Record<string, WalkSentence>;

export type WalkCopyId = keyof typeof WALK_COPY;

// ── THE WALK'S DRAFT A, the pre-spend purchase notice (LAUNCH-PF), approved 2026-10-05 ──────────────────────────────────
//
// Draft A was the app's (PURCHASE-FIRST, R182): the two sentences Tyler asked for when he ruled that the recent-purchase
// check comes before the credit — *"make sure we state that if there are other reasons for an appeal, it may still be
// valuable"*. Approved in the same reply as B and C, "Yes ABC" (WALK_DRAFTS_APPROVED), and shipped in the app as
// `PurchaseNoticeCopy` (AppealAce/Features/Flow/PurchaseNoticeCopy.swift). The website shows the same words at the same
// moment, so each is pinned twice by `test/purchaseNotice.test.tsx`: to the record's words, and to the app's literal on
// the line it cites. IF A PIN FAILS, DO NOT EDIT THE PIN.

export interface PurchaseNoticeSentence {
  readonly text: string;
  readonly status: "approved";
  readonly approval: typeof WALK_DRAFTS_APPROVED;
  readonly draft: "A";
  readonly batch: "PURCHASE-FIRST";
  /** `File.swift:line` under `AppealAce/`, where the app holds the same words. */
  readonly appSource: string;
}

function walkA(appSource: string, text: string): PurchaseNoticeSentence {
  return { text, status: "approved", approval: WALK_DRAFTS_APPROVED, draft: "A", batch: "PURCHASE-FIRST", appSource };
}

export const PURCHASE_NOTICE_COPY = {
  // A.1: `PurchaseNoticeCopy.otherGroundsMayStillBeWorthIt`.
  "purchaseNotice.otherGrounds": walkA(
    "Features/Flow/PurchaseNoticeCopy.swift:23",
    "Your purchase only weighs against an appeal on price. If your home has a condition problem, like a failing roof, or the county's record has something wrong, like your square footage or number of bedrooms, an appeal on those grounds may still be worth it.",
  ),
  // A.2: `PurchaseNoticeCopy.conditionOrRecordDetailBeforeSpend`, under "My home has a condition or record problem".
  "purchaseNotice.groundsDetail": walkA(
    "Features/Flow/PurchaseNoticeCopy.swift:28",
    "You'll tell us what's wrong after your credit is applied, and your appeal will rest on that, not on price.",
  ),
} as const satisfies Record<string, PurchaseNoticeSentence>;

export type PurchaseNoticeCopyId = keyof typeof PURCHASE_NOTICE_COPY;

// ── R-pending-web-free-first: THE FIRST LOOK'S LINK, option A, approved 2026-10-15 ──────────────────────────────────────────
//
// The band in `/checkout`'s "Before you pay" panel ends in one link that opens the whole free check in a pop-up. Its words
// were offered to Tyler on 2026-10-14 as two options: A "See everything from your free check ›" and B "See the full free
// check ›". On 2026-10-15 he chose: "A for link wording". A is the link's words below, word for word. B was only ever the
// alternative offered; it is recorded here and nowhere else, is not held as a sentence, and renders nowhere. Pinned word for
// word by `test/heldCopy.test.tsx`, section 15. IF A PIN FAILS, DO NOT EDIT THE PIN.
//
// Until this approval the link was a draft, outlined on the page (`data-draft-copy`) and held back by the server's
// FREE_CHECK_V2_ENABLED, registered `held` in functions/test/flags/registry.json. Both are gone with the approval: the
// outline, and the hold. The switch still defaults off; turning it on is a deploy through scripts/deploy.mjs, recorded on
// the ruling's row.

/** What Tyler said, and when. */
export const FREE_FIRST_LINK_APPROVED = { by: "Tyler", on: "2026-10-15", quote: "A for link wording" } as const;

export interface FreeFirstLinkSentence {
  readonly text: string;
  readonly status: "approved";
  readonly approval: typeof FREE_FIRST_LINK_APPROVED;
  /** The option Tyler chose, as the record offered it. */
  readonly option: "A";
}

export const FREE_FIRST_LINK_COPY = {
  "freeFirst.link.A": {
    text: "See everything from your free check ›", status: "approved", approval: FREE_FIRST_LINK_APPROVED, option: "A",
  },
} as const satisfies Record<string, FreeFirstLinkSentence>;

export type FreeFirstLinkId = keyof typeof FREE_FIRST_LINK_COPY;

/** The band's link: the one approved sentence. */
export const FREE_FIRST_LINK: FreeFirstLinkId = "freeFirst.link.A";

/** Any sentence this client holds, from any approval. */
export type AnyHeldId =
  | HeldCopyId | CreditBenefitId | ParitySentenceId | AppWordingId | AccountCopyId | AccountFollowUpId | AddPasswordId
  | WalkCopyId | PurchaseNoticeCopyId | ConfirmedPageId | CheckoutCreditsId | FreeFirstLinkId;

const HELD_TEXT: Readonly<Record<AnyHeldId, { readonly text: string }>> = {
  ...HELD_COPY, ...CREDIT_BENEFITS, ...PARITY_SENTENCES, ...APP_WORDING, ...ACCOUNT_COPY, ...ACCOUNT_FOLLOW_UP_COPY,
  ...ADD_PASSWORD_COPY, ...WALK_COPY, ...PURCHASE_NOTICE_COPY, ...CONFIRMED_PAGE_COPY, ...CHECKOUT_CREDITS_COPY,
  ...FREE_FIRST_LINK_COPY,
};

/** The words of one held sentence. The element that shows them carries `data-held-copy` with the id. */
export function heldText(id: AnyHeldId): string {
  return HELD_TEXT[id].text;
}

/** A held sentence with its `{name}` slots filled. A slot left unfilled is a thrown error, never printed braces. */
export function heldTemplate(id: AnyHeldId, values: Readonly<Record<string, string | number>>): string {
  return heldText(id).replace(/\{(\w+)\}/g, (_, name: string) => {
    if (!Object.hasOwn(values, name)) throw new Error(`heldTemplate(${id}): no value for {${name}}`);
    return String(values[name]);
  });
}
